import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ChevronDown, X } from 'lucide-react';
import { TrackFlytLiveButton } from './live/TrackFlytLive';
import LivePlayByPlay from './live/LivePlayByPlay';
import { usePausableInterval } from '../lib/usePausableInterval';
import { TeamHelmetPhoto } from './TeamHelmet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// iter229e · Team primary-color palette by ESPN team abbreviation.
// Used to render color-circle team badges INSTEAD of real ESPN logos —
// keeps the profile page identity-neutral (no real school logos, no
// real player headshots) while still telegraphing which teams are
// playing. Add more abbrs here as new schools get on-boarded.
//
// iter230 · Keys use uppercase ALPHANUMERIC ONLY (`&` / `-` / spaces
// stripped) so ESPN abbrs like "TA&M" / "MIA-OH" resolve to the same
// entry as the clean form. `teamColorFor()` normalizes the incoming
// abbr the same way before lookup.
const TEAM_COLOR_BY_ABBR = {
  // Big 12 · SEC · frequently-fetched opponents
  BAY:  '#154734', BU:   '#154734',   // Baylor
  AUB:  '#0C2340',                    // Auburn
  UT:   '#BF5700', TEX:  '#BF5700',   // Texas
  TAMU: '#500000', TAM:  '#500000',   // Texas A&M ("TA&M" → "TAM" after normalize)
  SMU:  '#C8102E',                    // SMU
  TTU:  '#CC0000',                    // Texas Tech
  TCU:  '#4D1979',                    // TCU
  OU:   '#841617',                    // Oklahoma
  OSU:  '#FF7300',                    // Oklahoma State
  KU:   '#0051BA',                    // Kansas
  KSU:  '#512888',                    // Kansas State
  ISU:  '#C8102E',                    // Iowa State
  UCF:  '#000000',                    // UCF
  BYU:  '#002E5D',                    // BYU
  CIN:  '#E00122',                    // Cincinnati
  HOU:  '#C8102E',                    // Houston
  WVU:  '#002855',                    // West Virginia
  UGA:  '#BA0C2F',                    // Georgia
  ALA:  '#9E1B32',                    // Alabama
  LSU:  '#461D7C',                    // LSU
  FLA:  '#0021A5',                    // Florida
  UK:   '#0033A0',                    // Kentucky
  MISS: '#CE1126',                    // Ole Miss
  MSST: '#5D1725',                    // Miss State
  MO:   '#F1B82D',                    // Missouri
  ARK:  '#9D2235',                    // Arkansas
  SC:   '#73000A',                    // South Carolina
  TENN: '#FF8200',                    // Tennessee
  VAN:  '#000000',                    // Vanderbilt
  // iter230 · Additional commonly-scheduled opponents
  MIAOH: '#B61E2E',                   // Miami (OH) RedHawks ("MIA-OH" / "MOST")
  MOST: '#B61E2E',                    // ESPN sometimes emits MOST for Miami-Ohio
  UTSA: '#0C2340',                    // UTSA
  RICE: '#00205B',                    // Rice
  UTEP: '#FF6C00',                    // UTEP
  NTX:  '#00853E',                    // North Texas
  NDSU: '#009A44',                    // North Dakota State (occasional FCS opener)
  ND:   '#0C2340',                    // Notre Dame
  USC:  '#990000',                    // USC
  UCLA: '#2D68C4',                    // UCLA
  ORE:  '#154733',                    // Oregon
  WASH: '#4B2E83',                    // Washington
  STAN: '#8C1515',                    // Stanford
  CAL:  '#003262',                    // Cal
  OSUB: '#BB0000', OHIOST: '#BB0000', OHST: '#BB0000',  // Ohio State
  MICH: '#00274C',                    // Michigan
  PSU:  '#041E42',                    // Penn State
  WIS:  '#C5050C',                    // Wisconsin
  IOWA: '#000000',                    // Iowa (all-black kit primary)
  MINN: '#7A0019',                    // Minnesota
};

const teamColorFor = (team = {}) => {
  const rawAbbr = (team.abbr || '').toUpperCase();
  // Normalize away ESPN's punctuation quirks — "TA&M", "MIA-OH", "N.C.STATE"
  // all collapse to letters+digits so a single palette entry serves them.
  const cleanAbbr = rawAbbr.replace(/[^A-Z0-9]/g, '');
  return (
    team.color                                   // backend-provided (rare)
    || TEAM_COLOR_BY_ABBR[rawAbbr]
    || TEAM_COLOR_BY_ABBR[cleanAbbr]
    || '#3b82f6'                                 // flyt blue fallback
  );
};

/**
 * TeamBadgeCircle — real recolored-photo football helmet in the team's
 * color, with the abbreviation decal'd on. Was a flat radial-gradient
 * disk; now uses the same TeamHelmetPhoto chip as Daily Brief / the
 * Live Now home strip, fed this file's own team-color palette.
 */
const TeamBadgeCircle = ({ team = {}, size = 32 }) => {
  const color = teamColorFor(team);
  const abbr = team.abbr || team.name || '?';
  const className = size >= 28 ? 'w-8 h-8 shrink-0' : 'w-5 h-5 shrink-0';
  return <TeamHelmetPhoto color={color} abbr={abbr} className={className} sizePx={size} />;
};

/**
 * PlayerHelmetCircle — iter816 · Jay: "make these players be helmets
 * too, with their number on it." Same TeamHelmetPhoto chip/decal
 * TeamBadgeCircle already uses for the team-level chips, just fed the
 * player's own jersey number as the decal instead of the team
 * abbreviation — no new asset, no new drawing logic, just a different
 * label on the same crown-decal slot. Falls back to the plain initials
 * disk when there's no jersey number to show at all (a data gap, not
 * the common case).
 */
const PlayerHelmetCircle = ({ player = {}, size = 32 }) => {
  const jersey = String(player.jersey || '').trim();
  if (!jersey) return <PlayerInitialsCircle player={player} size={size} />;
  const color = teamColorFor(player.team || {});
  const className = size >= 48 ? 'w-14 h-14 shrink-0' : size >= 28 ? 'w-8 h-8 shrink-0' : 'w-5 h-5 shrink-0';
  return <TeamHelmetPhoto color={color} abbr={jersey} className={className} sizePx={size} />;
};
/**
 * PlayerInitialsCircle — school-color disk with the player's
 * first+last initials (e.g. "DM" for Dre'lon Miller). Drops real ESPN
 * headshots per Jay's identity-neutral direction on the profile page.
 * Kept as PlayerHelmetCircle's own fallback for a player with no
 * jersey number on file.
 */
const PlayerInitialsCircle = ({ player = {}, size = 32 }) => {
  const initials = (player.name || `${player.first || ''} ${player.last || ''}`)
    .trim().split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';
  const color = teamColorFor(player.team || {});
  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0"
      style={{
        width: size,
        height: size,
        background: `radial-gradient(circle at 30% 25%, ${color} 0%, ${color}dd 60%, ${color}aa 100%)`,
        boxShadow: `inset 0 0 0 1px ${color}, 0 4px 10px rgba(0,0,0,0.35)`,
      }}
      aria-hidden
    >
      <span
        className="font-display font-black text-white leading-none tracking-tight"
        style={{ fontSize: `${size * 0.36}px` }}
      >
        {initials}
      </span>
    </div>
  );
};

/**
 * LiveGamePill
 * ─────────────────────────────────────────────────────────────
 * A liquid-glass dark-Apple-UI status pill that sits beneath an
 * athlete's profile header. If the athlete's linked college team
 * is playing RIGHT NOW, it shows a live scoreboard (clock at top,
 * quarter at bottom, athlete-team score left, opponent right). If
 * the team isn't currently playing, it shows the next scheduled
 * game in a dimmed static state. It fully hides when the athlete
 * has no linked school, no upcoming game, or during the off-season.
 *
 * Data is fetched from GET /api/live-game?school=<name> which
 * proxies ESPN's team endpoint and returns:
 *   { status: 'live', clock, period, athlete_team, opponent, is_home }
 *   { status: 'upcoming', opponent, athlete_team, date_label, time_label, is_home }
 *   { status: 'none' }  → nothing to render.
 *
 * College football only for now — starts with TCU, Texas A&M,
 * Texas. Backend adds more team-ids as we onboard schools.
 */
const LiveGamePill = ({ school, initialGame = null }) => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  // Roster preview — collapsed by default so the pill stays compact.
  // On first expand we fetch both team rosters in parallel and cache
  // them on this component instance; subsequent toggles are instant.
  const [expanded, setExpanded] = useState(false);
  const [rosters, setRosters] = useState({ home: null, opp: null });
  const [rosterLoading, setRosterLoading] = useState(false);
  // Tap-through mini card — a single player at a time. Detail is
  // lazy-fetched from /api/player-detail on first tap so we never make
  // 20 upfront calls.
  const [openPlayer, setOpenPlayer] = useState(null);         // seed row from roster
  const [openDetail, setOpenDetail] = useState(null);         // enriched ESPN response
  const [openDetailLoading, setOpenDetailLoading] = useState(false);

  // iter368 · If the profile page already fetched the live_game (MLB
  // pro athletes, etc.) hydrate directly and skip the CFB-only school
  // lookup. This is how Ozzie Albies' Braves games show up on his
  // profile card — his athlete row has no `school` value, so the
  // school-based fetch below never fires.
  const hasInitialGame = !!(initialGame && initialGame.status && initialGame.status !== 'none');
  const proGameSport = hasInitialGame ? String(initialGame.sport || '').toLowerCase() : '';
  const proTeamId = hasInitialGame ? initialGame.athlete_team_id : null;
  // iter478 · Keep MLB / other non-CFB live scores fresh. The profile
  // page's initialGame is a point-in-time snapshot — if we don't
  // re-poll, Ozzie's card is frozen at whatever inning the page
  // loaded on. When the payload includes both a sport and a team_id,
  // poll `/live-game` every 30s while the game stays live.
  const isProLive = hasInitialGame && proGameSport && proGameSport !== 'cfb' && proTeamId && initialGame.status === 'live';
  const isSchoolPath = !hasInitialGame && !!school;

  useEffect(() => {
    if (hasInitialGame) {
      setData(initialGame);
      setLoaded(true);
      return;
    }
    if (!school) setLoaded(true);
  }, [school, initialGame, hasInitialGame]);

  const pollProGame = useCallback(() => {
    axios
      .get(`${API}/live-game`, { params: { sport: proGameSport, team_id: proTeamId } })
      .then((r) => {
        if (r.data && r.data.status !== 'none') {
          // Preserve `sport` so subsequent polls keep working even if
          // the backend response omits it.
          setData({ sport: proGameSport, ...r.data });
        }
      })
      .catch(() => { /* silent — keep stale data on transient failure */ });
  }, [proGameSport, proTeamId]);
  // No `immediate` fire here — initialGame already hydrated `data`
  // synchronously above, matching the original behavior of only
  // polling for FOLLOW-UP updates, not an extra fetch on mount.
  usePausableInterval(pollProGame, 30000, { enabled: isProLive, immediate: false });

  const pollSchoolGame = useCallback(() => {
    axios
      .get(`${API}/live-game`, { params: { school, sport: 'cfb' } })
      .then((r) => {
        setData(r.data || null);
        setLoaded(true);
      })
      .catch(() => {
        setLoaded(true);
        setData({ status: 'none' });
      });
  }, [school]);
  // While a game is live the pill refreshes every 30s so the clock
  // stays roughly current — paused while the app is backgrounded.
  usePausableInterval(pollSchoolGame, 30000, { enabled: isSchoolPath });

  // While the mini card is open on a LIVE game, refetch the player's
  // stat line every 30s so numbers stay current with the play clock.
  // Off-live: no polling — the last-game data doesn't change.
  const openPlayerLive = !!(openPlayer && openPlayer._isLive && openPlayer.id && data?.event_id);
  const tickOpenPlayer = useCallback(async () => {
    if (!openPlayerLive) return;
    try {
      const r = await axios.get(`${API}/player-detail/${encodeURIComponent(openPlayer.id)}`, {
        params: { event_id: data.event_id },
      });
      if (r.data) setOpenDetail(r.data);
    } catch { /* silent */ }
  }, [openPlayerLive, openPlayer?.id, data?.event_id]);
  usePausableInterval(tickOpenPlayer, 30000, { enabled: openPlayerLive, immediate: false });

  // iter253 · Prefetch both team rosters as soon as game data lands so
  // the stats sheet opens instantly (<500ms) on first tap. Pill renders
  // once per profile page so this costs exactly two cached requests.
  // iter386 · CFB-only. The roster resolver is college-football-specific
  // (matches team nicknames to NCAAF team_ids). Calling it for an MLB
  // game like "Braves vs Marlins" mislabels unrelated Division-II
  // football players as the MLB roster. Skip for non-CFB payloads
  // until dedicated MLB/NFL/NBA roster support ships.
  const fetchRosters = async () => {
    if (!data || rosters.home || rosterLoading) return;
    const gameSport = String(data.sport || 'cfb').toLowerCase();
    if (gameSport !== 'cfb') {
      // Not football — leave rosters empty so the expander UI can
      // gracefully hide/skip the stat-sheet section.
      setRosterLoading(false);
      return;
    }
    const homeName = (data.athlete_team && data.athlete_team.name) || school;
    const oppName = data.opponent && data.opponent.name;
    if (!oppName) return;
    setRosterLoading(true);
    try {
      const [homeR, oppR] = await Promise.all([
        axios.get(`${API}/team-roster`, { params: { school: homeName, sport: 'cfb' } }),
        axios.get(`${API}/team-roster`, { params: { school: oppName, sport: 'cfb' } }),
      ]);
      setRosters({
        home: (homeR.data && homeR.data.players) || [],
        opp:  (oppR.data && oppR.data.players)  || [],
      });
    } catch {
      setRosters({ home: [], opp: [] });
    } finally {
      setRosterLoading(false);
    }
  };

  useEffect(() => {
    if (data && ['live', 'upcoming', 'bye'].includes(data.status)) fetchRosters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!loaded || !data || data.status === 'none') return null;

  const isLive = data.status === 'live';
  const isBye = data.status === 'bye';
  const athlete = data.athlete_team || {};
  const opponent = data.opponent || {};

  // iter459i · Score normalizer. ESPN's MLB scoreboard sometimes hands
  // back the score as a linescore object `{value: 0.0, displayValue: "0"}`
  // instead of a scalar (their internal linescore format leaks through
  // when the game is in the pre-first-pitch "warmup" window). React
  // coerces the raw object → the literal `{'value': 0.0, 'displayValue': '0'}`
  // string, which is what Jay caught on the Braves/Yankees card.
  // Handles: number, string, `{displayValue}`, `{value}`, null → '0'.
  const scoreText = (raw) => {
    if (raw === null || raw === undefined) return '0';
    if (typeof raw === 'number' || typeof raw === 'string') return String(raw);
    if (typeof raw === 'object') {
      if (raw.displayValue !== undefined && raw.displayValue !== null) return String(raw.displayValue);
      if (raw.value !== undefined && raw.value !== null) {
        const n = Number(raw.value);
        return Number.isFinite(n) ? String(Math.trunc(n)) : '0';
      }
    }
    return '0';
  };

  // First-time expand kicks off both roster fetches in parallel (already
  // prefetched on mount — this is just the fallback path).
  const handleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next) fetchRosters();
  };

  // Open a player's mini card. Uses the roster row we already have
  // (for instant paint) and enriches asynchronously with the last-game
  // stat line from /api/player-detail. When the game is currently
  // LIVE, passes the event_id so the backend also returns the player's
  // in-progress boxscore stat line.
  // iter816 · Jay: stats only ever showed up for a player who already
  // had an `athletes` row on our own backend — anyone else (most of
  // both rosters, on most games) opened to a bare bio with nothing.
  // The roster fetch already hands us this player's name/team/position/
  // jersey straight from ESPN, so pass those along too: the backend can
  // now look the player up in CFBD directly by name+team when it has no
  // matching athletes row, instead of giving up.
  const openMiniCard = async (row, team) => {
    setOpenPlayer({ ...row, _team: team, _isLive: isLive });
    setOpenDetail(null);
    if (!row?.id) return;
    setOpenDetailLoading(true);
    try {
      const r = await axios.get(`${API}/player-detail/${encodeURIComponent(row.id)}`, {
        params: {
          ...(isLive && data.event_id ? { event_id: data.event_id } : {}),
          name: row.name || `${row.first || ''} ${row.last || ''}`.trim(),
          team: team?.name || '',
          position: row.position || '',
          jersey: row.jersey || '',
        },
      });
      setOpenDetail(r.data || null);
    } catch { /* mini card falls back to roster-only fields */ }
    finally { setOpenDetailLoading(false); }
  };
  const closeMiniCard = () => { setOpenPlayer(null); setOpenDetail(null); };

  return (
    <div
      // iter229i · Match the athlete profile card's outer width so the
      // Upcoming/Live game card and the athlete card read as one cohesive
      // stack on iPad + desktop. Both use `max-w-3xl` at ≥sm with the
      // same 16-24px horizontal padding — mobile stays snug.
      className="mx-auto max-w-md sm:max-w-3xl w-full my-3 px-4 sm:px-6"
      data-testid={`live-game-pill-${data.status}`}
    >
      {/* iter330 · "Track FLYT Live" pill sitting ON TOP of the game
          card. Tapping opens the Uber-style permission sheet that
          starts an iOS Live Activity + drops the same scoreboard on
          the user's Lock Screen and (via the widget) Home Screen.
          Hidden during bye weeks — nothing to track. */}
      {!isBye && (
        <div className="flex justify-center mb-2" data-testid="live-game-track-slot">
          <TrackFlytLiveButton
            variant="inline"
            game={{
              game_id: data.event_id || `${school}-${data.status}`,
              team_slug: school,
              team_name: athlete.name || school,
              opponent: opponent.name || opponent.abbr || '',
              kickoff_iso: data.kickoff_iso || data.start_time || '',
              home: (data.is_home ? athlete.abbr : opponent.abbr) || 'HOME',
              away: (data.is_home ? opponent.abbr : athlete.abbr) || 'AWAY',
              // iter694 · Seed the Lock Screen card with the CURRENT
              // live score so it shows the real number immediately —
              // the backend then rewrites it every 10s via APNs.
              home_score: Number(scoreText(data.is_home ? athlete.score : opponent.score)) || 0,
              away_score: Number(scoreText(data.is_home ? opponent.score : athlete.score)) || 0,
              clock: data.clock || '',
              period: (typeof data.period === 'number' ? data.period : parseInt(String(data.period || '').match(/\d+/)?.[0] || '0', 10)) || 0,
              status: isLive ? 'in_progress' : (data.status || 'scheduled'),
              sport: data.sport || 'cfb',
            }}
          />
        </div>
      )}
      <div
        className={`relative rounded-3xl ring-1 backdrop-blur-2xl backdrop-saturate-150 overflow-hidden ${
          isLive
            ? 'bg-black/70 ring-white/15 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.6)]'
            : 'bg-black/50 ring-white/10 shadow-[0_12px_36px_-16px_rgba(0,0,0,0.5)]'
        }`}
      >
        {/* Subtle animated glow — only on live so the eye is drawn */}
        {isLive && (
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none opacity-40"
            style={{
              background:
                'radial-gradient(60% 60% at 50% 50%, rgba(239,68,68,0.18) 0%, transparent 70%)',
            }}
          />
        )}
        <div className={`relative px-4 py-3 ${isLive ? '' : 'opacity-90'}`}>
          {/* TOP CENTER — clock (live) / date (upcoming) / BYE tag */}
          <div className="text-center">
            <p
              className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] font-black ${
                isLive ? 'text-red-400' : isBye ? 'text-amber-300/80' : 'text-white/50'
              }`}
            >
              {isLive ? (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  {/* iter478 · Clock is only meaningful for football /
                      basketball. MLB (and other inning-based sports)
                      send an empty clock — the period label already
                      encodes state (e.g. "End 6th"), so we hide the
                      redundant "0:00" / "—" separator. */}
                  {data.clock ? <>Live &middot; {data.clock}</> : <>Live</>}
                </>
              ) : isBye ? (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-300/80" />
                  Bye Week · Back {data.date_label || 'Soon'}
                </>
              ) : (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
                  Upcoming · {data.date_label || 'TBD'}
                </>
              )}
            </p>
          </div>

          {/* MAIN ROW — team score / vs / opponent score */}
          <div className="mt-2 grid grid-cols-3 items-center gap-2">
            {/* Athlete team — left */}
            <div className="flex items-center gap-2 min-w-0">
              <TeamBadgeCircle team={athlete} size={32} />
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold truncate">
                  {data.is_home ? 'Home' : 'Away'}
                </p>
                <p className="font-display uppercase text-white text-sm leading-none tracking-tight truncate">
                  {athlete.abbr || athlete.name || '—'}
                </p>
              </div>
            </div>

            {/* Center score block — score-score for live, "vs / @" for upcoming */}
            <div className="text-center">
              {isLive ? (
                <p className="font-display text-white text-2xl sm:text-3xl leading-none tracking-tight tabular-nums">
                  <span>{scoreText(athlete.score)}</span>
                  <span className="mx-1.5 text-white/30">·</span>
                  <span>{scoreText(opponent.score)}</span>
                </p>
              ) : (
                <p className="font-display uppercase text-white/70 text-lg leading-none tracking-tight">
                  {data.is_home ? 'vs' : '@'}
                </p>
              )}
            </div>

            {/* Opponent — right */}
            <div className="flex items-center gap-2 justify-end min-w-0">
              <div className="min-w-0 text-right">
                <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold truncate">
                  Opp
                </p>
                <p className="font-display uppercase text-white text-sm leading-none tracking-tight truncate">
                  {opponent.abbr || opponent.name || '—'}
                </p>
              </div>
              <TeamBadgeCircle team={opponent} size={32} />
            </div>
          </div>

          {/* BOTTOM CENTER — quarter (live) / kickoff (upcoming) / next-opp (bye) */}
          <div className="mt-2 text-center">
            <p className="text-[10px] uppercase tracking-[0.28em] font-black text-white/60">
              {isLive
                ? (data.period || 'In progress')
                : isBye
                  ? `Next: ${data.is_home ? 'vs' : '@'} ${opponent.abbr || opponent.name || 'TBD'}`
                  : (data.time_label || 'Kickoff TBD')}
            </p>
          </div>

          {/* EXPAND CHEVRON — bottom-right corner. Tapping reveals the
              top-10 players from each team so fans get a "who's on the
              field" preview without leaving the profile. */}
          <button
            type="button"
            onClick={handleExpand}
            className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-white/[0.06] ring-1 ring-white/10 text-white/70 hover:text-white hover:bg-white/[0.12] flex items-center justify-center transition"
            aria-label={expanded ? 'Hide rosters' : 'Show top 10 players from each team'}
            aria-expanded={expanded}
            data-testid="live-game-pill-expand"
          >
            <ChevronDown
              className="w-4 h-4"
              style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 180ms' }}
            />
          </button>
        </div>

        {/* Expanded roster panel — home + opponent side by side. Hidden
            by default; renders in-place inside the pill so nothing else
            on the page moves when the athlete's fan taps to expand. */}
        {expanded && (
          <div
            className="relative border-t border-white/10 px-3 py-4 sm:px-4"
            data-testid="live-game-rosters"
          >
            {rosterLoading && (
              <p className="text-[10px] uppercase tracking-widest text-white/40 text-center py-4">
                Loading rosters…
              </p>
            )}
            {!rosterLoading && (
              <>
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  <RosterColumn
                    team={athlete}
                    players={rosters.home}
                    emptyLabel="Roster unavailable"
                    align="left"
                    onSelect={(row) => openMiniCard(row, athlete)}
                  />
                  <RosterColumn
                    team={opponent}
                    players={rosters.opp}
                    emptyLabel="Roster unavailable"
                    align="right"
                    onSelect={(row) => openMiniCard(row, opponent)}
                  />
                </div>
                {/* Attribution footer — rebranded per founder (iter286) */}
                <p className="mt-4 text-center text-[9px] uppercase tracking-[0.32em] font-black text-white/30">
                  FLYT Live
                </p>
              </>
            )}
          </div>
        )}
        {/* Tap-through player mini card — sheet-style overlay pinned
            inside the pill so nothing else on the page shifts. */}
        {openPlayer && (
          <PlayerMiniCard
            seed={openPlayer}
            detail={openDetail}
            loading={openDetailLoading}
            onClose={closeMiniCard}
          />
        )}
      </div>

      {/* iter560 · Live Play-by-Play scroll (CFBD-licensed).
          Renders directly under the live-game card whenever a CFB
          game is in progress. Self-polls every 10s and auto-hides
          when the game ends or no plays return. */}
      {isLive && data.event_id && (data.sport || 'cfb') === 'cfb' && (
        <LivePlayByPlay
          gameId={data.event_id}
          active={isLive}
        />
      )}
    </div>
  );
};

export default LiveGamePill;

/**
 * RosterColumn — one side of the expanded rosters panel. Each row is
 * a two-line card: circular ESPN headshot on the outer edge, then
 * name on top and the YTD stat directly beneath it, with jersey /
 * position stacked on the inner edge. Left column mirrors right so
 * both teams read "team-first" from the outside in.
 */
const RosterColumn = ({ team = {}, players, emptyLabel = '—', align = 'left', onSelect }) => {
  const isRight = align === 'right';
  return (
    <div className={isRight ? 'text-right' : ''}>
      <div className={`flex items-center gap-2 mb-3 ${isRight ? 'justify-end' : ''}`}>
        {!isRight && <TeamBadgeCircle team={team} size={20} />}
        <p className="text-[10px] uppercase tracking-[0.24em] font-black text-white/70 truncate">
          {team.abbr || team.name || 'Team'}
        </p>
        {isRight && <TeamBadgeCircle team={team} size={20} />}
      </div>
      {(!players || players.length === 0) ? (
        <p className="text-[10px] text-white/40 py-2">{emptyLabel}</p>
      ) : (
        <ul className="space-y-2">
          {players.map((p) => (
            <li key={p.id || `${p.name}-${p.jersey}`}>
              <RosterRow player={p} team={team} isRight={isRight} onSelect={onSelect} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * RosterRow — a single tap-target. Fixed 44px min height for touch,
 * with headshot pinned to the outer edge (left col → left, right col
 * → right). Name + YTD stat stack vertically in the center; jersey +
 * position stack on the inner edge. Empty slots (no headshot) render
 * an initials avatar so the row never has a jarring blank circle.
 */
const RosterRow = ({ player: p, team, isRight, onSelect }) => {
  const avatar = (
    <PlayerHelmetCircle player={{ ...p, team }} size={32} />
  );
  const middle = (
    <div className={`min-w-0 flex-1 ${isRight ? 'text-right' : ''}`}>
      <p className={`text-[12px] leading-tight truncate font-semibold flex items-center gap-1.5 ${
        p.is_out ? 'text-white/55' : 'text-white'
      } ${isRight ? 'justify-end' : ''}`}>
        {p.is_out && isRight && (
          <span
            className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest bg-red-500/20 text-red-300 ring-1 ring-red-500/40"
            title={p.injury_status || 'Out'}
            data-testid={`roster-row-out-${p.id || p.jersey}`}
          >
            Out
          </span>
        )}
        <span className="truncate">{p.name || `${p.first} ${p.last}`}</span>
        {p.is_out && !isRight && (
          <span
            className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest bg-red-500/20 text-red-300 ring-1 ring-red-500/40"
            title={p.injury_status || 'Out'}
            data-testid={`roster-row-out-${p.id || p.jersey}`}
          >
            Out
          </span>
        )}
      </p>
      {p.primary_stat ? (
        <span className="inline-flex items-center gap-1 mt-0.5 text-[9px] font-black tracking-wider text-emerald-300/90 bg-emerald-500/10 ring-1 ring-emerald-500/20 rounded px-1.5 py-0.5">
          {p.primary_stat.value}
          <span className="text-white/50">{p.primary_stat.label}</span>
        </span>
      ) : (
        <span className="text-[10px] text-white/40 leading-none">
          {p.class ? p.class : '—'}
        </span>
      )}
    </div>
  );
  const inner = (
    <div className={`shrink-0 ${isRight ? 'text-left' : 'text-right'} min-w-[38px]`}>
      <p className={`text-[10px] font-black tracking-wider ${
        p.starter ? 'text-blue-200' : 'text-white/70'
      }`}>
        #{p.jersey || '—'}
      </p>
      <p className="text-[9px] uppercase tracking-widest text-white/40 leading-none mt-0.5">
        {p.position || '—'}
      </p>
    </div>
  );
  return (
    <button
      type="button"
      onClick={() => onSelect && onSelect({ ...p, team })}
      className={`w-full flex items-center gap-2 py-1.5 px-1.5 rounded-lg hover:bg-white/[0.06] transition ${
        isRight ? 'flex-row-reverse' : ''
      }`}
      data-testid={`roster-row-${p.id || p.jersey}`}
    >
      {avatar}
      {middle}
      {inner}
    </button>
  );
};

/**
 * PlayerMiniCard — sheet-style overlay pinned inside the LiveGamePill.
 * Renders instantly using the roster row we already have (headshot,
 * jersey, position, class, height, weight) then enriches with the
 * player's last completed game — OR their LIVE stat line if the pill
 * caller flagged the game as in-progress.
 */
const PlayerMiniCard = ({ seed, detail, loading, onClose }) => {
  const d = detail || {};
  // Pull bio/name from either the top-level payload (legacy shape)
  // or from `bio` (new CFBD-backed shape emitted by /player-detail).
  const bio = d.bio || {};
  const name = d.name || bio.name || seed.name || `${seed.first || ''} ${seed.last || ''}`.trim();
  const height = d.height || bio.height || seed.height;
  const weight = d.weight || bio.weight || seed.weight;
  const classYear = d.class || bio.class_year || seed.class;
  const position = bio.position || seed.position;
  const jersey = bio.jersey || seed.jersey;
  const lg = d.last_game;
  const seasonStats = d.season_stats || [];
  const fp = d.flythouse_profile;
  const isLive = Boolean(seed._isLive && d.live_stat);
  // iter569 · Convert PlayerMiniCard into a viewport-anchored bottom
  // sheet (mobile) / centered modal (sm+) instead of an `absolute
  // inset-0` overlay pinned inside the LiveGamePill. The old approach
  // stretched with the pill's expanded roster + play-by-play section
  // — on mobile that pushed the mini card open BELOW the fold.
  // iter570 · Always center the card in the visible viewport (was
  // docking to the bottom via `items-end` on mobile). Jay's TestFlight
  // report: tapping a player near the top of the screen required
  // scrolling down to see the card. `items-center` + a modest
  // `pt-safe`/`pb-safe` gutter keeps it in the reader's eye line at
  // any scroll depth.
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-3 py-6 bg-black/70 backdrop-blur-md animate-in fade-in duration-150"
      onClick={onClose}
      data-testid="player-mini-card"
    >
      <div
        className="relative w-full sm:max-w-md max-h-[80vh] overflow-y-auto bg-neutral-950 rounded-3xl ring-1 ring-white/15 shadow-[0_18px_60px_rgba(0,0,0,0.7)] p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close player card"
          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/[0.08] ring-1 ring-white/10 text-white/80 hover:text-white hover:bg-white/[0.14] flex items-center justify-center transition z-10"
          data-testid="player-mini-card-close"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        {/* HEADER — school-color helmet chip (player's own number) + identity (identity-neutral, no real headshot) */}
        <div className="flex items-center gap-3 pr-10">
          <PlayerHelmetCircle player={{ name, team: seed.team, jersey }} size={56} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              {jersey && (
                <span
                  className={`shrink-0 px-1.5 rounded font-black text-[10px] tracking-wider relative ${
                    seed.starter ? 'bg-blue-500/25 text-blue-200' : 'bg-white/[0.08] text-white/70'
                  }`}
                >
                  #{jersey}
                  {isLive && (
                    <span aria-hidden className="absolute inset-0 rounded ring-2 ring-red-500/70 animate-ping" />
                  )}
                </span>
              )}
              <span className="text-[10px] uppercase tracking-widest font-black text-white/60 truncate">
                {position}{classYear ? ` · ${classYear}` : ''}
              </span>
            </div>
            <p className="font-display text-base leading-tight text-white truncate">{name}</p>
            <p className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">
              {[height, weight].filter(Boolean).join(' · ') || '—'}
            </p>
          </div>
        </div>

        {/* SEE ON FLYTHOUSE — Apple-style liquid-glass CTA. Renders
            below the header at full width when we found a matching
            profile on our network. If we didn't, we surface a subtle
            "Not yet on Flythouse — claim your profile" hint so the
            player-viewing-themselves has a clear path in. */}
        {fp && fp.url ? (
          <Link
            to={fp.url}
            onClick={onClose}
            className="mt-3 w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-2xl bg-white/[0.06] ring-1 ring-white/15 backdrop-blur-2xl backdrop-saturate-150 text-white hover:bg-white/[0.10] hover:ring-white/25 transition"
            data-testid="player-mini-card-see-on-network"
          >
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-black">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              See on Flythouse
              {fp.verified && (
                <span className="text-[9px] text-emerald-300 normal-case tracking-normal font-semibold">· Verified</span>
              )}
            </span>
            <ChevronDown className="w-4 h-4 -rotate-90 text-white/60" />
          </Link>
        ) : (
          <div
            className="mt-3 w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-2xl bg-white/[0.03] ring-1 ring-white/10 text-white/70"
            data-testid="player-mini-card-claim-hint"
          >
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-black">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
              Not on Flythouse yet
            </span>
            <Link
              to={`/claim/espn/${encodeURIComponent(seed.id || '')}?name=${encodeURIComponent(name)}&sport=${encodeURIComponent(seed.sport || '')}&position=${encodeURIComponent(seed.position || '')}&jersey=${encodeURIComponent(seed.jersey || '')}&class=${encodeURIComponent(seed.class || '')}&height=${encodeURIComponent(seed.height || '')}&weight=${encodeURIComponent(seed.weight || '')}&school_slug=${encodeURIComponent(seed.team?.slug || '')}&school_name=${encodeURIComponent(seed.team?.name || seed.team?.abbr || '')}`}
              onClick={onClose}
              className="text-[10px] uppercase tracking-widest font-black text-blue-300 hover:text-blue-200"
            >
              Claim →
            </Link>
          </div>
        )}

        {/* Season primary stat — prefer the fresh CFBD lookup
            from /player-detail (`d.primary_stat`); fall back to
            the roster-row snapshot (`seed.primary_stat`) when
            the detail fetch is still loading. */}
        {(d?.primary_stat || seed.primary_stat) && (
          <div className="mt-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 ring-1 ring-emerald-500/20 text-emerald-300 text-[10px] font-black uppercase tracking-widest">
            {(d?.primary_stat || seed.primary_stat).label} · {(d?.primary_stat || seed.primary_stat).value}
          </div>
        )}

        {/* LIVE stat line — replaces last-game while game is in progress */}
        {isLive && (
          <div
            className="mt-3 rounded-xl bg-red-500/10 ring-1 ring-red-500/25 px-3 py-2"
            data-testid="player-mini-card-live-stats"
          >
            <p className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] font-black text-red-300 mb-1">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              Live · {d.live_stat.category || 'Stats'}
            </p>
            <p className="text-xs text-white leading-tight font-semibold">
              {d.live_stat.summary || 'No stats recorded yet'}
            </p>
          </div>
        )}

        {/* LAST GAME — now with labeled stat grid */}
        {!isLive && (
          <div className="mt-3">
            {loading && (
              <p className="text-[10px] uppercase tracking-widest text-white/40">Loading last game…</p>
            )}
            {!loading && lg && lg.opponent && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.28em] font-black text-white/50 mb-1">Last Game</p>
                <p className="text-xs text-white/85 leading-tight">
                  {lg.score ? `${lg.score} ` : ''}vs {lg.opponent}
                </p>
                {Array.isArray(lg.labeled_stats) && lg.labeled_stats.length > 0 && (
                  <div className="mt-2 grid grid-cols-3 gap-1.5">
                    {lg.labeled_stats.slice(0, 9).map((s, i) => (
                      <div key={i} className="rounded-lg bg-white/[0.04] ring-1 ring-white/10 px-2 py-1.5">
                        <p className="text-[9px] uppercase tracking-widest text-white/40 truncate">{s.label || s.name}</p>
                        <p className="text-sm font-black text-white leading-tight">{s.value}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* SEASON TOTALS — every category ESPN publishes for the player */}
        {seasonStats.length > 0 && (
          <div className="mt-3">
            <p className="text-[10px] uppercase tracking-[0.28em] font-black text-white/50 mb-2">
              Season · Year to Date
            </p>
            <div className="space-y-2">
              {seasonStats.map((cat, i) => (
                <div key={i}>
                  <p className="text-[9px] uppercase tracking-widest text-white/40 mb-1">{cat.category}</p>
                  <div className="grid grid-cols-4 gap-1">
                    {cat.stats.map((s, j) => (
                      <div key={j} className="rounded-md bg-white/[0.04] ring-1 ring-white/10 px-1.5 py-1">
                        <p className="text-[9px] uppercase tracking-widest text-white/40 truncate leading-none">{s.label || s.name}</p>
                        <p className="text-xs font-black text-white leading-tight truncate mt-0.5">{s.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
